import { BraintreeComponent } from '@3ddv/software-division-components';
import { BraintreeTrigger } from '@3ddv/software-division-components/generic/braintree';
import { CommonModule, CurrencyPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as countryJson from '@assets/data/country-code.json';
import * as subCountryJson from '@assets/data/sub-country-code.json';
import { StickyProgressBarComponent } from '@core/components/sticky-progress-bar/sticky-progress-bar.component';
import { TimerComponent } from '@core/components/timer/timer.component';
import { FooterComponent } from '@core/layout/footer/footer.component';
import { ModalsService } from '@core/modals/modals.service';
import {
  Configuration,
  CountryList,
  PatronAddressModel,
  PatronModel,
  Seat,
  SubCountryItem,
  SubCountryObject,
  TransactionModel,
  TransactionWrapper,
  Upgrade,
  Upsell,
} from '@core/models';
import { AdobeDataPages } from '@core/models/adobe-data-layer.model';
import { OktaIntegrationModule } from '@core/okta-integration/okta-integration.module';
import {
  ApiException,
  CartService,
  ConfigurationService,
  UtilitiesService,
} from '@core/services';
import {
  ANALYTICS_ABANDON_OWNERS,
  ANALYTICS_CANCEL_REASONS,
  ANALYTICS_EVENTS,
  AnalyticsService,
} from '@core/services/analytics';
import { DemoDataService } from '@core/services/demo-data.service';
import { GroupLinkService } from '@core/services/group-link.service';
import { TransactionService } from '@core/services/transaction.service';
import { environment } from '@environments/environment';
import { UserClaims } from '@okta/okta-auth-js';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { UpgradesService } from '../select-seat/viewer/topbar/upgrades/upgrades.service';
import { AdobeDataLayerService } from './../../../core/services/adobe-data-layer-service.service';
import { buildTransactionItems } from './_utils/transaction-items.builder';
import { CartComponent } from './components/cart/cart.component';
import { CheckoutRecapComponent } from './components/checkout-recap/checkout-recap.component';
import { ConfirmationPageComponent } from './components/confirmation-page/confirmation-page.component';
import { GroupOutingComponent } from './components/group-outing/group-outing.component';
import { PatronComponent } from './components/patron/patron.component';
import { RainoutInfoComponent } from './components/rainout-info/rainout-info.component';
import { OrderSummaryComponent } from './components/summary/order-summary.component';
import { PaymentDetails } from './models/payment-details.model';
import { ConnectionService } from './services/connection.service';

/**
 * provenue-connect appCode for a phone number ProVenue will not accept. Every ticketing provider
 * failure shares one domain code, so this is what tells us the phone field is the one at fault.
 */
const PV_INVALID_PHONE_NUMBER_FORMAT = 'PV_INVALID_PHONE_NUMBER_FORMAT';

type ComponentPaymentDetails = Omit<
  PaymentDetails,
  'paymentMethod' | 'cardType' | 'firstName' | 'lastName' | 'email' | 'address'
> & {
  paymentMethod: string | null;
  cardType: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  address:
    | (Omit<PatronAddressModel, 'city' | 'postalCode' | 'address1'> & {
        city: string | null;
        postalCode: string | null;
        address1: string | null;
      })
    | null;
  phone: {
    phone: string;
    countryCode: string;
  } | null;
};

/**
 * Unified checkout + payment screen.
 *
 * Formerly two separate routes/pages (`/checkout/:id` and `/payment/:id`) with an
 * intermediate "Proceed to Payment" navigation between them. Merged into a single
 * `/checkout/:id` route/page: all business
 * logic from both former components is preserved as-is, only the navigation step
 * between them was removed and their templates were combined into one scrollable page.
 */
@Component({
  imports: [
    CommonModule,
    FormsModule,
    RainoutInfoComponent,
    TimerComponent,
    TooltipModule,
    CartComponent,
    GroupOutingComponent,
    OrderSummaryComponent,
    ConfirmationPageComponent,
    OktaIntegrationModule,
    PatronComponent,
    CheckoutRecapComponent,
    BraintreeComponent,
    StickyProgressBarComponent,
    FooterComponent,
  ],
  standalone: true,
  providers: [CurrencyPipe],
  selector: 'app-checkout',
  styleUrls: ['./checkout.component.css'],
  templateUrl: './checkout.component.html',
})
export class CheckoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private adobeDataLayerService = inject(AdobeDataLayerService);
  private cartService = inject(CartService);
  private connectionService = inject(ConnectionService);
  private activatedRoute = inject(ActivatedRoute);
  private modalsService = inject(ModalsService);
  public configurationService = inject(ConfigurationService);
  private transactionService = inject(TransactionService);
  private upgradesService = inject(UpgradesService);
  private utilitiesService = inject(UtilitiesService);
  protected demoDataService = inject(DemoDataService);
  private groupLinkService = inject(GroupLinkService);
  private cd = inject(ChangeDetectorRef);
  private currencyPipe = inject(CurrencyPipe);
  private analyticsService = inject(AnalyticsService);

  @ViewChild('paymentForm') form: NgForm;

  deadlineInMilliseconds: number;
  eventDate: number;
  isRainout: boolean;
  nowDate: number = Date.now();
  seats: Seat[];
  selectedBuyerTypeId: number;
  totalTransactionSeats: number;
  transactionId: string;
  upsellSelected: Upsell | null;
  transaction = signal<TransactionWrapper | null>(null);
  public subscriptions: Subscription[] = [];
  public isTicketFeeEnabled = signal(false);
  public isDiscountCodesEnabled = signal(false);

  // --- Payment (formerly PaymentComponent) state ---
  patron: PatronModel;
  apiUrl = environment.apiUrl;
  data: { transaction: TransactionModel | null; btToken: string } = {
    transaction: null,
    btToken: '',
  };
  /**
   * Lets `submitPayment()` ask the Braintree drop-in for a nonce directly, now
   * that the drop-in's own "Use this card" button is gone — the footer CTA is
   * the single submit affordance.
   */
  readonly braintreeTrigger = new BraintreeTrigger();
  countryList: CountryList = [];
  subCountryObject: SubCountryObject = {};
  subCountryList: SubCountryItem[] = [];
  paymentDetails: ComponentPaymentDetails = {
    nonce: null,
    paymentMethod: null,
    cardType: null,
    acceptedTerms: false,
    firstName: null,
    lastName: null,
    email: null,
    digitalWallet: null,
    accountCredit: {
      credits: 0,
      payFull: false,
    },
    address: null,
    phone: null,
  };
  displayCredits: string = '0.00';
  confirmationPage: boolean;

  /**
   * True only when PV returned a complete address at OKTA association — same snapshot logic must not depend on live form values or typing would lock fields mid-edit.
   */
  patronAddressLocked = false;

  /**
   * True only when PV returned a complete phone at OKTA association.
   */
  patronPhoneLocked = false;

  get rainoutValue(): boolean {
    const transaction = this.transaction();
    if (!transaction) {
      return false;
    } else {
      return !!transaction?.transaction?.tdcTransaction?.hasInsuranceRainout;
    }
  }

  get isDiscountActive(): boolean {
    const transaction = this.transaction();
    if (!transaction) {
      return false;
    }
    // Matches discount-code.component.ts's `hasDiscount` check on the same field — `!== null`
    // treated '' and undefined as active, diverging from that other check on '' or a removed code.
    return !!transaction.transaction.discountCode;
  }

  get isSelectedUpsellNotEditable(): boolean {
    return this.upsellSelected?.editable === false;
  }

  /**
   * True when the "Need more Fun?" upsell section renders. Mirrors the condition that
   * guards `<app-group-outing>` in the template; the shared top row uses it to decide
   * which heading sits there (see checkout.component.html).
   */
  get hasUpsells(): boolean {
    return !!(
      this.configurationService.configuration.upsells &&
      this.transaction()?.transaction?.event?.upsells?.length
    );
  }

  set rainoutValue(value) {
    const transaction = this.transaction();

    if (!transaction) {
      return;
    }

    Object.assign(
      (transaction.transaction as Required<TransactionModel>).tdcTransaction,
      { hasInsuranceRainout: Boolean(value) }
    );
  }

  get configuration() {
    return this.configurationService.configuration;
  }

  get substractCreditsToBalance(): number {
    const balance =
      this.transaction()?.transaction.tdcTransaction?.balance || 0;
    const result = balance - this.paymentDetails.accountCredit.credits;
    return result > 0 ? result : 0;
  }

  get showCreditsApplies(): number {
    const credits = this.paymentDetails.accountCredit.credits;
    return credits && credits > 0 ? credits : 0;
  }

  /**
   * Drives the checkout CTA's green/dark-gray state (see StickyProgressBarComponent's
   * `success` ctaVariant). Mirrors submitPayment()'s own guard clauses below, MINUS the
   * card/digital-wallet/pay-full check: Braintree's hosted fields only report validity at
   * tokenization time, via `braintreeTrigger.requestPayment()` — an async call that itself
   * triggers the drop-in's payment-method flow, so there is no live signal to read here
   * without actually attempting a submit. This is therefore a "the rest of the form is
   * filled in" indicator, not a guarantee the payment itself will go through — submitPayment()
   * still runs the real checks (including the card one) before ever calling the API.
   */
  get canSubmitPayment(): boolean {
    const configuration = this.configurationService.configuration;
    const balance =
      this.transaction()?.transaction?.tdcTransaction?.balance || 0;
    const { credits } = this.paymentDetails.accountCredit;

    if (credits > balance) {
      return false;
    }
    if (!this.paymentDetails.acceptedTerms) {
      return false;
    }
    const extraCheckboxIsRequiredAndActive =
      configuration.extraCheckbox &&
      configuration.extraCheckbox.active &&
      configuration.extraCheckbox.required;
    if (
      !this.paymentDetails.extraCheckbox &&
      extraCheckboxIsRequiredAndActive
    ) {
      return false;
    }
    if (
      !this.paymentDetails.firstName ||
      !this.paymentDetails.lastName ||
      !this.paymentDetails.email
    ) {
      return false;
    }
    if (this.form?.form?.controls['email']?.invalid) {
      return false;
    }
    if (this.checkAddressInputs(configuration)) {
      return false;
    }
    if (this.checkPhoneInputs(configuration.custom.paymentPage.capturePhone)) {
      return false;
    }
    if (
      configuration.rainout &&
      this.rainoutValue === null &&
      this.eventDate - this.nowDate > this.deadlineInMilliseconds
    ) {
      return false;
    }

    return true;
  }

  ngOnInit(): void {
    this.confirmationPage = false;
    const transaction = this.activatedRoute.snapshot.data[
      'transaction'
    ] as TransactionWrapper;
    this.transactionService.transaction$.next(transaction);
    this.transactionId = this.activatedRoute.snapshot.params['id'];
    this.isRainout = this.configurationService.configuration.rainout;

    this.connectionService.token =
      this.activatedRoute.snapshot.queryParams['token'];

    this.paymentDetails.paymentMethod =
      this.configurationService.configuration.paymentMethod;

    this.initClientFlags(transaction);

    this.initializeComponent(transaction);
    this.initializePaymentFormDefaults();

    this.adobeDataLayerService.pageEventSubject.subscribe(async page => {
      if (page !== AdobeDataPages.Checkout) {
        return;
      }
      const ticket_addons = [
        {
          name: this.transactionService.getTransactionUpsell()?.name || '',
          price:
            this.transactionService.getTransactionUpsell()?.price?.toString() ||
            '0',
        },
      ];

      if (this.transactionService.hasRainoutInsurance()) {
        ticket_addons.push({
          name: 'rainout insurance',
          price: this.transactionService.getInsurancePrice()?.toString(),
        });
      }
      const gamePk = await this.adobeDataLayerService.getGamePK();
      const eventCommonPayload = {
        event_id: this.adobeDataLayerService.getEventId(),
        matchup_full: this.adobeDataLayerService.getMatchupFull(),
        game_pk: gamePk,
        ticket_quantity: this.transactionService.getTotalSeats()?.toString(),
        ticket_total_price: this.transactionService.getTotalPrice()?.toString(),
        ticket_unit_price: this.transactionService.getUnitPrice()?.toString(),
        ticket_seat_section: this.cartService.getSeatsSections()?.[0] || '',
        ticket_addons,
      };

      // Checkout and Payment used to be two separate page views (each firing its
      // own analytics event). They are now a single page, so both events fire
      // together off of the one "Checkout" page-view instead.
      this.adobeDataLayerService.push({
        event: 'cart-view',
        ticketing: { events: [eventCommonPayload] },
      });
      this.adobeDataLayerService.push({
        event: 'begin-checkout',
        ticketing: { events: [eventCommonPayload] },
      });
    });
  }

  ngAfterViewInit(): void {
    // form_start: first dirty valueChanges, once per journey (guarded by AnalyticsService).
    this.form.form.valueChanges
      ?.pipe(
        filter(() => !!this.form.dirty),
        take(1)
      )
      .subscribe(() => this.analyticsService.trackFormStart('checkout'));
  }

  ngOnDestroy(): void {
    // Real in-app navigation away (not a tab close, which the pagehide flush handles).
    // Still armed means the transaction was left on hold — a genuine checkout abandonment.
    if (
      this.analyticsService.isPendingAbandonArmed(
        ANALYTICS_ABANDON_OWNERS.CHECKOUT
      )
    ) {
      this.analyticsService.trackAbandon({
        stage: 'payment',
        transactionId: this.transactionId,
        gameId: this.currentGameId(),
        value: this.transaction()?.transaction?.tdcTransaction?.balance,
        seatCount: this.totalTransactionSeats,
      });
    }
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  initializeComponent(transaction: TransactionWrapper) {
    if (!transaction) {
      const modalData = {
        title: 'ERROR',
        content: 'An Error occurred while trying to get the Transaction.',
        acceptBtnName: 'CLOSE',
        acceptFunction: () =>
          this.onTransactionExpiredAction(
            ANALYTICS_CANCEL_REASONS.TRANSACTION_NOT_FOUND
          ),
      };
      this.modalsService.openModal(modalData);
      return;
    }

    this.transaction.set({ ...transaction });
    this.data = transaction;

    this.onTransactionChange(); // calculate seats by row and so on; also (re-)arms checkout_abandoned
    // the first time put the value to null to force the user to select one
    if (!transaction.transaction.tdcTransaction?.hasInsuranceRainout) {
      (
        transaction.transaction as Required<TransactionModel>
      ).tdcTransaction.hasInsuranceRainout = false;
    }
    // Rainout deadline
    if (transaction.transaction.event?.date) {
      this.eventDate = new Date(transaction.transaction.event.date).getTime();
    }
    this.deadlineInMilliseconds = 21 * 24 * 60 * 60 * 1000; // 21 days

    // Upgrades
    this.upgradesService.upgrades = transaction.transaction.event
      ?.upgrades as Upgrade[];
    this.upgradesService.checkCartSeatsForUpgradesList(
      this.totalTransactionSeats
    );

    this.setUpsellSelected(this.upsellSelected);
  }

  /** Former PaymentComponent.initializeComponent() — sets up the payment form defaults. */
  initializePaymentFormDefaults() {
    this.countryList = (countryJson as any)['default'];
    this.subCountryObject = (subCountryJson as any)['default'];
    if (this.configurationService.configuration.extraCheckbox)
      this.paymentDetails['extraCheckbox'] = false;
    if (this.configuration.custom.paymentPage.captureAddress) {
      this.paymentDetails.address = {
        subCountryCode: '',
        city: null,
        countryCode: 'USA',
        postalCode: null,
        address1: null,
      };
      this.subCountryList = this.getSortedSubCountryList(
        this.paymentDetails.address.countryCode
      );
    }
    if (this.configuration.custom.paymentPage.capturePhone) {
      this.paymentDetails.phone = {
        phone: '',
        countryCode: 'USA',
      };
    }
  }

  onRainoutChange(rainoutValue: boolean) {
    // if rainout no and we have rainout price
    const transaction = this.transaction();
    const previousInsurancePrice = (
      transaction?.transaction as Required<TransactionModel>
    ).tdcTransaction?.insuranceRainoutPrice;

    if (!rainoutValue && previousInsurancePrice) {
      const subscription = this.connectionService
        .deleteRainoutInsuranceToTransaction(this.transactionId)
        .subscribe({
          next: data => {
            if (transaction?.transaction) {
              transaction.transaction = data.data as TransactionModel;
              this.transaction.set({ ...transaction });
              this.onTransactionChange();
              this.analyticsService.trackRainoutInsuranceToggled({
                action: 'removed',
                insurancePrice: previousInsurancePrice,
                transactionId: this.transactionId,
              });
            }
          },
          error: error => {
            this.rainoutValue = true;
            console.error(error);
            const modalData = {
              title: 'ERROR',
              content:
                'An Error occurred while trying to delete Rainout Insurance, please try again.',
              acceptBtnName: 'CLOSE',
            };
            // If there's a custom api error.
            if (error instanceof ApiException) {
              modalData.content = error.message;
            }
            this.modalsService.openModal(modalData);

            this.analyticsService.trackError({
              eventName: ANALYTICS_EVENTS.CHECKOUT_ERROR,
              errorType: 'rainout_remove_failed',
              errorMessage:
                error instanceof ApiException && error.message
                  ? error.message
                  : 'An Error occurred while trying to delete Rainout Insurance, please try again.',
              transactionId: this.transactionId,
              gameId: this.currentGameId(),
            });
          },
        });

      this.subscriptions.push(subscription);
    } else if (rainoutValue) {
      const subscription = this.connectionService
        .addRainoutInsuranceToTransaction(this.transactionId)
        .subscribe({
          next: data => {
            if (transaction?.transaction) {
              transaction!.transaction = data.data as TransactionModel;
              this.transaction.set({ ...transaction });
              this.onTransactionChange();
              const newInsurancePrice = (
                data.data as Required<TransactionModel>
              ).tdcTransaction?.insuranceRainoutPrice;
              this.analyticsService.trackRainoutInsuranceToggled({
                action: 'added',
                insurancePrice: newInsurancePrice ?? 0,
                transactionId: this.transactionId,
              });
            }
          },
          error: error => {
            this.rainoutValue = false;
            console.error(error);
            const modalData = {
              title: 'ERROR',
              content:
                'An Error occurred while trying to add Rainout Insurance, please try again.',
              acceptBtnName: 'CLOSE',
            };
            // If there's a custom api error.
            if (error instanceof ApiException) {
              modalData.content = error.message;
            }
            this.modalsService.openModal(modalData);

            this.analyticsService.trackError({
              eventName: ANALYTICS_EVENTS.CHECKOUT_ERROR,
              errorType: 'rainout_add_failed',
              errorMessage:
                error instanceof ApiException && error.message
                  ? error.message
                  : 'An Error occurred while trying to add Rainout Insurance, please try again.',
              transactionId: this.transactionId,
              gameId: this.currentGameId(),
            });
          },
        });

      this.subscriptions.push(subscription);
      this.cd.detectChanges();
    }
  }

  onChangeUpsell(radio: HTMLInputElement) {
    const transaction = this.transaction();
    if (!transaction) {
      return;
    }

    let buyerTypeId = parseInt(radio.value, 10);
    let actualBuyerType = transaction.transaction.buyerTypeId;
    if (actualBuyerType !== buyerTypeId) {
      let buyerTypeName = '';
      radio.checked = true;
      // get the buyer type name to send with buyer type id
      if (transaction.transaction.event?.upsells?.values()) {
        for (const buyerTypeInfo of transaction.transaction.event.upsells.values()) {
          if (buyerTypeInfo.buyerTypeId == buyerTypeId) {
            buyerTypeName = buyerTypeInfo.buyerTypeName as string;
          }
        }
      }
      // put the same rainout insurance value again to take into account the user has not selected yes or no
      const previousRainoutValue = (
        transaction.transaction as Required<TransactionModel>
      ).tdcTransaction.hasInsuranceRainout;
      this.editBuyerType(previousRainoutValue, buyerTypeId, buyerTypeName);
    } else {
      // TODO: I guess this default buyer type can come from transaction.originalBuyerType or transaction.eventData.defaultBuyerType
      // to be able to delete the event_id relation in the transaction
      const defaultBuyerTypeId = (
        transaction.transaction as Required<TransactionModel>
      ).event.defaultBuyerType;
      const defaultBuyerTypeName = (
        transaction.transaction as Required<TransactionModel>
      ).event.defaultBuyerTypeName;
      const previousRainoutValue = (
        transaction.transaction as Required<TransactionModel>
      ).tdcTransaction.hasInsuranceRainout;
      // request to api
      this.editBuyerType(
        previousRainoutValue,
        defaultBuyerTypeId as number,
        defaultBuyerTypeName as string
      );
      this.selectedBuyerTypeId = 0;
      radio.checked = false;
    }

    return;
  }

  /**
   * NOT the header Back button — that calls TransactionService.restartTransaction() directly
   * (see header.component.ts goBack(), which is where checkout_back is tracked). This method's
   * only caller is the upsell-PATCH-failure error modal's accept action (editBuyerType() below):
   * an API failure forces the transaction to be torn down, so it's reported as its own cancel
   * reason rather than mislabeled as a user-initiated checkout_back.
   */
  restartTransaction() {
    this.analyticsService.setPendingCancelReason(
      ANALYTICS_CANCEL_REASONS.UPSELL_CHANGE_ERROR
    );
    // Same reasoning as onTransactionExpiredAction()/restartTransactionAfterError(): this is a
    // cancellation, not an abandonment — disarm so ngOnDestroy doesn't also fire checkout_abandoned.
    this.analyticsService.setPendingAbandon(
      ANALYTICS_ABANDON_OWNERS.CHECKOUT,
      null
    );

    const subscription = this.connectionService
      .deleteTransaction(this.transactionId)
      .subscribe({
        next: response => {
          this.goSeatSelection(true);
        },
        error: error => {
          // no need for a modal here
          this.goSeatSelection(true);
        },
      });
    this.subscriptions.push(subscription);
  }

  goSeatSelection(restoreCart: boolean = false) {
    const transaction = this.transaction();

    let eventId: number | null = null;
    if (transaction) {
      eventId = (transaction.transaction as Required<TransactionModel>).event
        .pvEventId;
    }
    this.router.navigate(
      this.groupLinkService.prependGroupLink(['seat-selection']),
      {
        queryParams: {
          // null (omitted from the URL) when the transaction never loaded — the
          // transaction_not_found path used to send `event=0`, and the seat-selection
          // resolver then reported a bogus page_view with event=0.
          event: eventId,
          'restore-cart': restoreCart ? '1' : null,
          token: null,
          demo: this.demoDataService.isDemoActive() ? 'true' : null,
        },
        queryParamsHandling: 'merge',
      }
    );
  }

  onTransactionExpired() {
    this.analyticsService.trackError({
      eventName: ANALYTICS_EVENTS.PAYMENT_TIMEOUT,
      errorType: 'cart_expired',
      errorMessage:
        'The checkout timer expired before the transaction completed',
      transactionId: this.transactionId,
      gameId: this.currentGameId(),
    });

    const modalData = {
      title: 'Transaction expired',
      content: 'The transaction has expired, please try again.',
      acceptBtnName: 'CLOSE',
      acceptFunction: () =>
        this.onTransactionExpiredAction(ANALYTICS_CANCEL_REASONS.CART_EXPIRED),
    };
    this.modalsService.openModal(modalData);
  }

  onTransactionExpiredAction(
    reason: string = ANALYTICS_CANCEL_REASONS.CART_EXPIRED
  ) {
    this.analyticsService.setPendingCancelReason(reason);
    // This is a cancellation (transaction_cancelled fires on the resulting navigation via
    // the reason above), not an abandonment — disarm so ngOnDestroy doesn't also fire
    // checkout_abandoned for the same occurrence.
    this.analyticsService.setPendingAbandon(
      ANALYTICS_ABANDON_OWNERS.CHECKOUT,
      null
    );

    const subscription = this.connectionService
      .deleteTransaction(this.transactionId)
      .subscribe({
        next: response => {
          this.goSeatSelection();
        },
        error: error => {
          // no need for a modal here
          this.goSeatSelection();
        },
      });
    this.subscriptions.push(subscription);
  }

  /** pvEventId of the loaded transaction's event, as a string — analytics-only. */
  private currentGameId(): string | undefined {
    const pvEventId = this.transaction()?.transaction?.event?.pvEventId;
    return pvEventId !== undefined ? String(pvEventId) : undefined;
  }

  /**
   * (Re-)arms checkout_abandoned for the transaction currently on hold, disarmed on
   * submitPayment() success. Called from onTransactionChange() — which runs after every
   * checkout-page mutation (rainout add/remove, upsell change, discount apply/remove) as
   * well as on initial load — so the armed value/seatCount never goes stale after one of
   * those mutations. Reported either by the pagehide flush (tab closed) or, in ngOnDestroy,
   * when the flow is left in-app without ever completing
   */
  private armCheckoutAbandon(): void {
    this.analyticsService.setPendingAbandon(ANALYTICS_ABANDON_OWNERS.CHECKOUT, {
      stage: 'payment',
      transactionId: this.transactionId,
      gameId: this.currentGameId(),
      value: this.transaction()?.transaction?.tdcTransaction?.balance,
      seatCount: this.totalTransactionSeats,
    });
  }

  onDiscountCodeChange(updatedTransaction: TransactionWrapper) {
    this.transaction.set({ ...updatedTransaction });
    this.onTransactionChange();
  }

  // executed when we retrieve the transaction again
  onTransactionChange() {
    // Provisional data for checkout templating
    const transaction = this.transaction();
    if (!transaction) {
      return;
    }
    this.seats = [];
    this.upsellSelected = null;
    // create structure for frontend
    if (transaction.transaction.seatsPrices) {
      const seatsPricesEntries = Object.entries(
        transaction.transaction.seatsPrices
      );

      for (const [mmcId, price] of seatsPricesEntries) {
        let seat = this.utilitiesService.splittedSeatInfoFromId(mmcId) as Seat;
        seat.price = price.value;
        this.seats.push(seat);
      }
    }

    this.totalTransactionSeats = this.seats.length;
    if (transaction.transaction.event?.upsells?.values()) {
      for (const upsellInfo of transaction.transaction.event.upsells.values()) {
        if (upsellInfo.buyerTypeId == transaction.transaction.buyerTypeId) {
          this.upsellSelected = upsellInfo;
        }
      }
    }
    this.setUpsellSelected(this.upsellSelected);
    this.transactionService.transaction$.next(transaction);
    // Re-arm with the just-recomputed value/seatCount — this runs after every mutation that
    // can change them (rainout, upsell, discount), not just on initial load.
    this.armCheckoutAbandon();
  }

  /**
   * Sets the club flags that decide how the order summary renders (fee rows, discount input).
   * The route resolver already loaded the client, so the flags are set before the first render.
   * Starting from `false` and waiting for a second request made the summary flash the generic
   * "Total ticket fees" row, then swap it for the fee rows. The request only runs when the
   * resolver could not load the client.
   */
  private initClientFlags(transaction: TransactionWrapper | null): void {
    const client = transaction?.client;
    if (client) {
      this.isTicketFeeEnabled.set(!!client.isTicketFeeEnabled);
      this.isDiscountCodesEnabled.set(!!client.isDiscountCodesEnabled);
      return;
    }

    const getCurrentClient$ = this.connectionService
      .getCurrentClient()
      .subscribe(response => {
        this.isTicketFeeEnabled.set(!!response.data?.isTicketFeeEnabled);
        this.isDiscountCodesEnabled.set(
          !!response.data?.isDiscountCodesEnabled
        );
      });

    this.subscriptions.push(getCurrentClient$);
  }

  private editBuyerType(
    previousRainoutValue: boolean,
    buyerTypeId: number,
    buyerTypeName: string
  ) {
    const previousCartId =
      this.transaction()?.transaction?.tdcTransaction?.cartId;
    // The upsell being LEFT, if any — the deselect half of upsell_selected (its
    // inverse-action gap, see UPSELL_SELECTED in analytics.events.ts). Captured pre-PATCH
    // because the successful response rewrites this.upsellSelected to the new buyer type.
    const previousUpsell = this.upsellSelected;

    const subscription = this.connectionService
      .editTransactionBuyerType(this.transactionId, buyerTypeId, buyerTypeName)
      .subscribe({
        next: data => {
          const transaction = this.transaction();
          if (!transaction) {
            return;
          }
          transaction.transaction = data.data as TransactionModel;
          // put the same rainout insurance value again to take into account the user has not selected yes or no
          Object.assign(
            (transaction.transaction as Required<TransactionModel>)
              .tdcTransaction,
            { hasInsuranceRainout: previousRainoutValue }
          );
          this.transaction.set({ ...transaction });
          this.onTransactionChange();
          this.trackUpsellSelectedIfApplicable(
            transaction.transaction,
            previousRainoutValue,
            previousCartId,
            previousUpsell
          );
        },
        error: error => {
          console.error(error);
          const modalData = {
            title: 'ERROR',
            content: 'An Error occurred while trying to change upsells',
            acceptBtnName: 'CLOSE',
            acceptFunction: () => undefined,
          };
          // If there's a custom api error.
          if (error instanceof ApiException) {
            modalData.content = error.message;

            modalData['acceptFunction'] = () => {
              this.restartTransaction();
            };
          }
          this.modalsService.openModal(modalData);

          this.analyticsService.trackError({
            eventName: ANALYTICS_EVENTS.CHECKOUT_ERROR,
            errorType: 'upsell_change_failed',
            errorMessage:
              error instanceof ApiException && error.message
                ? error.message
                : 'An Error occurred while trying to change upsells',
            transactionId: this.transactionId,
            gameId: this.currentGameId(),
            value: this.transaction()?.transaction?.tdcTransaction?.balance,
          });
        },
      });

    this.subscriptions.push(subscription);
  }

  /**
   * Fires the right half of the upsell pair for the buyer-type PATCH: upsell_selected when
   * the new buyer type matches a real upsell, upsell_deselected (keyed on the upsell being
   * left) when it matches none — the reset-to-default branch of onChangeUpsell — and the
   * transaction was previously ON one. A default→default no-op reports nothing. Both carry
   * previous_cart_id/new_cart_id only when rainout was inactive: that is the only PATCH path
   * where TransactionCartResetter actually swaps the underlying Provenue cart.
   */
  private trackUpsellSelectedIfApplicable(
    transaction: TransactionModel,
    previousRainoutValue: boolean,
    previousCartId: number | undefined,
    previousUpsell: Upsell | null
  ): void {
    const upsell = transaction.event?.upsells?.find(
      candidate => candidate.buyerTypeId === transaction.buyerTypeId
    );
    // Either the upsell just moved ONTO (selected) or the one just moved OFF (deselected);
    // default→default with no prior upsell (neither) reports nothing.
    const reportedUpsell = upsell ?? previousUpsell;
    if (!reportedUpsell) {
      return;
    }

    const parameters = {
      upsellId: String(reportedUpsell.id),
      upsellName: reportedUpsell.name,
      value: reportedUpsell.price ?? 0,
      gameId: this.currentGameId() ?? '',
      transactionId: this.transactionId,
      ...(!previousRainoutValue && {
        previousCartId,
        newCartId: transaction.tdcTransaction?.cartId,
      }),
    };

    if (upsell) {
      this.analyticsService.trackUpsellSelected(parameters);
    } else {
      this.analyticsService.trackUpsellDeselected(parameters);
    }
  }

  getEnvironment(): boolean {
    return environment.production;
  }

  setUpsellSelected(upsell: Upsell | null) {
    if (upsell) {
      this.upsellSelected = upsell;
    } else {
      this.upsellSelected = null;
    }
  }

  // --- Payment (formerly PaymentComponent) methods ---

  onAddDigitalWallet() {
    this.modalsService.openAddDigitalWalletModal(
      this.patron,
      this.setDigitalWalletList.bind(this)
    );
  }

  setDigitalWalletList(
    digitalWalletList: PatronModel['digitalWalletList']
  ): void {
    this.patron.digitalWalletList = digitalWalletList;
  }

  onSubmit(submitData: {
    nonce: string;
    type: string;
    details: { cardType: string };
  }) {
    this.paymentDetails.nonce = submitData.nonce;
    this.paymentDetails.cardType = submitData.details.cardType;
  }

  /** Final CTA of the merged page — was PaymentComponent.checkout(). */
  async submitPayment() {
    if (this.demoDataService.isDemoActive()) {
      this.router.navigate(
        this.groupLinkService.prependGroupLink([
          '',
          'summary',
          this.transactionId,
        ]),
        {
          queryParams: {
            token: this.connectionService.token,
            demo: this.demoDataService.isDemoActive() ? 'true' : null,
          },
          queryParamsHandling: 'merge',
        }
      );
      return;
    }

    const configuration = this.configurationService.configuration;
    const balance =
      this.transaction()?.transaction?.tdcTransaction?.balance || 0;
    this.paymentDetails.paymentMethod =
      this.configurationService.configuration.paymentMethod;
    const { credits, payFull } = this.paymentDetails.accountCredit;

    // The drop-in no longer has its own submit button, so tokenize here. On
    // failure fall through to the guard below, which shows the existing
    // "Please enter a valid credit card." modal.
    if (!this.paymentDetails.digitalWallet && !payFull) {
      try {
        const payload = await this.braintreeTrigger.requestPayment();
        this.paymentDetails.nonce = payload.nonce;
        this.paymentDetails.cardType = payload.details.cardType;
      } catch (error: unknown) {
        // Clear any nonce from a previous successful tokenize — otherwise a stale
        // nonce could be reused after the card data was edited and failed to
        // re-tokenize, silently charging the wrong card details.
        this.paymentDetails.nonce = null;
        console.warn(error);
      }
    }

    if (
      !this.paymentDetails.nonce &&
      !this.paymentDetails.digitalWallet &&
      !payFull
    ) {
      const content = this.patron
        ? 'Please enter a valid credit card, select a digital wallet or pay all with credit. '
        : 'Please enter a valid credit card.';
      const modalData = {
        title: 'Payment information',
        content: content,

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      this.trackCheckoutValidationError('payment_method_missing', content);
      return;
    }

    if (credits > balance) {
      const content = 'The credit must be less than or equal to the balance.';
      const modalData = {
        title: 'Account Credit',
        content,

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      this.trackCheckoutValidationError('credit_exceeds_balance', content);
      return;
    }
    if (!this.paymentDetails.acceptedTerms) {
      const content = 'Please accept the terms and conditions.';
      const modalData = {
        title: 'Accept terms',
        content,

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      this.trackCheckoutValidationError('terms_not_accepted', content);
      return;
    }
    const extraCheckboxIsRequiredAndActive =
      configuration.extraCheckbox &&
      configuration.extraCheckbox.active &&
      configuration.extraCheckbox.required;
    if (
      !this.paymentDetails.extraCheckbox &&
      extraCheckboxIsRequiredAndActive
    ) {
      const modalData = {
        title: 'Accept Broker Polcy',
        content: 'Please accept the Brokers Policy.',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      this.trackCheckoutFormInvalid(['extraCheckbox']);
      return;
    }

    const missingContactFields = [
      !this.paymentDetails.firstName && 'firstName',
      !this.paymentDetails.lastName && 'lastName',
      !this.paymentDetails.email && 'email',
    ].filter((field): field is string => typeof field === 'string');
    if (missingContactFields.length > 0) {
      const modalData = {
        title: 'Required fields',
        content: 'You have to enter first name, last name and email.',
        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      this.trackCheckoutFormInvalid(missingContactFields);
      return;
    }
    if (this.form.form.controls['email'].invalid) {
      const modalData = {
        title: 'Valid email',
        content: 'Please enter a valid email.',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      this.trackCheckoutFormInvalid(['email']);
      return;
    }

    if (this.checkAddressInputs(configuration)) {
      const modalData = {
        title: 'Required fields',
        content: 'You must enter your address data',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      this.trackCheckoutFormInvalid(['address']);
      return;
    }

    if (this.checkPhoneInputs(configuration.custom.paymentPage.capturePhone)) {
      const modalData = {
        title: 'Required fields',
        content: 'You must enter your phone data',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      this.trackCheckoutFormInvalid(['phone']);
      return;
    }

    const transactionSnapshot = this.transaction()?.transaction;
    const items = transactionSnapshot
      ? buildTransactionItems(transactionSnapshot, this.seats)
      : [];
    this.analyticsService.trackAddShippingInfo({
      currency: 'USD',
      value: balance,
      items,
    });
    this.analyticsService.trackAddPaymentInfo(
      this.resolvePaymentType(payFull),
      balance,
      'USD',
      items
    );

    const subscription = this.connectionService
      .checkoutTransaction(
        this.transactionId,
        this.paymentDetails as PaymentDetails
      )
      .subscribe({
        next: response => {
          this.confirmationPage = true;
          this.analyticsService.setPendingAbandon(
            ANALYTICS_ABANDON_OWNERS.CHECKOUT,
            null
          );
          // tdcTransactionId is only ever set by this checkout call — it's null/undefined on
          // the pre-submit snapshot — so it must be read from the response, not the snapshot.
          const tdcTransactionId =
            response.data?.tdcTransaction?.tdcTransactionId ?? undefined;
          this.trackCompletedSale(transactionSnapshot, items, tdcTransactionId);
          setTimeout(() => {
            this.confirmationPage = false;
            this.router.navigate(
              this.groupLinkService.prependGroupLink([
                '',
                'summary',
                this.transactionId,
              ]),
              {
                queryParams: { token: this.connectionService.token },
                queryParamsHandling: 'merge',
              }
            );
          }, 4000);
        },

        error: error => {
          const modalData = {
            title: 'ERROR',
            content:
              'An Error occurred while trying to checkout the Transaction.',
            acceptBtnName: 'CLOSE',
          };

          if (error instanceof ApiException) {
            modalData.content = error.message;

            // ProVenue rejected the phone number, so whatever PV had on file is not usable. The
            // field is locked whenever PV returned one at OKTA association, which would leave the
            // user reading an error about a field they cannot edit: unlock it so they can fix it.
            if (error.data?.['pvCode'] === PV_INVALID_PHONE_NUMBER_FORMAT) {
              this.patronPhoneLocked = false;
            }
          }
          this.modalsService.openModal(modalData);

          this.analyticsService.trackError({
            eventName: ANALYTICS_EVENTS.PAYMENT_ERROR,
            errorType: 'confirm_transaction',
            errorMessage:
              error instanceof ApiException && error.message
                ? error.message
                : 'Failed to confirm the transaction',
            transactionId: this.transactionId,
            gameId: this.currentGameId(),
            value: balance,
          });
        },
      });

    this.subscriptions.push(subscription);
  }

  /** 'digital_wallet' > 'account_credit' > 'card', matching the priority submitPayment itself uses. */
  private resolvePaymentType(payFull: boolean): string {
    if (this.paymentDetails.digitalWallet) {
      return 'digital_wallet';
    }
    if (payFull) {
      return 'account_credit';
    }
    return 'card';
  }

  /**
   * checkout_form_invalid for one blocked submit: called by each field-level guard in
   * submitPayment() right where its condition fails, so the reported fields are the ones
   * the patron was actually stopped on (the modal's own copy) with no mirrored second
   * implementation of the checks. The business-rule guards (payment method, credit,
   * terms, discount) report checkout_validation_error instead and never reach here.
   */
  private trackCheckoutFormInvalid(invalidFields: string[]): void {
    this.analyticsService.trackCheckoutFormInvalid({
      invalidFields,
      gameId: this.currentGameId(),
      transactionId: this.transactionId,
      value: this.transaction()?.transaction?.tdcTransaction?.balance,
    });
  }

  private trackCheckoutValidationError(
    errorType: string,
    message: string
  ): void {
    this.analyticsService.trackError({
      eventName: ANALYTICS_EVENTS.CHECKOUT_VALIDATION_ERROR,
      errorType,
      errorMessage: message,
      transactionId: this.transactionId,
      gameId: this.currentGameId(),
    });
  }

  private trackCompletedSale(
    transaction: TransactionModel | undefined,
    items: ReturnType<typeof buildTransactionItems>,
    tdcTransactionId?: string | number
  ): void {
    if (!transaction) {
      return;
    }
    const tdcTransaction = transaction.tdcTransaction;

    const parameters = {
      transactionId: this.transactionId,
      // Prefer the id from the fresh checkout response (caller-supplied) — the pre-submit
      // snapshot's tdcTransaction never has it set yet.
      tdcTransactionId:
        tdcTransactionId ?? tdcTransaction?.tdcTransactionId ?? undefined,
      gameId: this.currentGameId(),
      itemCount: this.totalTransactionSeats,
      seatCount: this.totalTransactionSeats,
      hasUpsell: !!this.upsellSelected,
      hasRainoutInsurance: !!tdcTransaction?.hasInsuranceRainout,
      discountApplied: !!transaction.discountCode,
      value: tdcTransaction?.balance ?? 0,
      fees: tdcTransaction?.serviceCharges ?? 0,
      currency: 'USD',
    };

    this.analyticsService.trackCheckoutCompleted(parameters);
    this.analyticsService.trackPurchase({ ...parameters, items });
  }

  /**
   * Should check phone inputs if is needed
   * @since 1.4.0
   * @version 1.4.0
   * @param shouldCapturePhone should i capture phone
   * @returns all is good
   */
  private checkPhoneInputs(shouldCapturePhone: boolean): boolean {
    if (!shouldCapturePhone || !this.paymentDetails.phone) {
      return false;
    }
    const p = this.paymentDetails.phone;
    const phone = (p.phone ?? '').trim();
    const countryCode = (p.countryCode ?? '').trim();
    return !phone || !countryCode;
  }

  /**
   * Only check address inputs if capture address are activate and patron dont exist
   * @param configuration Club Configuration
   * @returns {boolean}
   * @version 1.3.1
   * @since 1.3.1
   */
  private checkAddressInputs(configuration: Configuration): boolean {
    if (
      !configuration.custom.paymentPage.captureAddress ||
      !this.paymentDetails.address
    ) {
      return false;
    }
    const a = this.paymentDetails.address;
    const address1 = (a.address1 ?? '').trim();
    const city = (a.city ?? '').trim();
    const countryCode = (a.countryCode ?? '').trim();
    const postalCode = (a.postalCode ?? '').trim();
    const subCountryCode = (a.subCountryCode ?? '').trim();
    return !address1 || !city || !countryCode || !postalCode || !subCountryCode;
  }

  showErrorModal(message: string) {
    const modalData = {
      title: 'Payment information',
      content: message,
      acceptBtnName: 'CLOSE',
    };
    this.modalsService.openModal(modalData);
    return;
  }

  infoEmailModal() {
    const clientName = this.configurationService.configuration.clientName;
    const modalData = {
      title: 'MLB Ballpark App',
      content: `${clientName} tickets are available exclusively as mobile tickets via the free MLB Ballpark app. To view and use tickets, log in to your existing MLB.com account or create an account in the MLB Ballpark app with the email used to purchase your ticket(s). Detailed instructions on using the MLB Ballpark app, including email verification, viewing tickets, and forwarding tickets, are available at <a href="${this.configurationService.configuration.clientBallParkUrl}" target="_blank">${this.configurationService.configuration.clientBallParkText}</a>.`,
      acceptBtnName: 'CLOSE',
    };
    this.modalsService.openModal(modalData);
  }

  oktaEventEmitter(instance: UserClaims): void {
    let patron = instance;
    if (!patron && this.demoDataService.isDemoActive()) {
      this.patron = patron;
      this.patronAddressLocked = false;
      this.patronPhoneLocked = false;
      return;
    }
    const subscription = this.connectionService
      .postAssociatePatronToCart(
        patron.sub,
        patron.email as string,
        parseInt(this.transactionId, 10),
        this.connectionService.token
      )
      .subscribe({
        next: response => {
          this.patron = response.data as PatronModel;
          this.paymentDetails.firstName = this.patron.firstName;
          this.paymentDetails.lastName = this.patron.lastName;
          this.paymentDetails.email = this.patron.email;

          const customPayment = this.configuration.custom.paymentPage;

          this.patronAddressLocked =
            !!customPayment.captureAddress &&
            this.isPvAddressComplete(this.patron.address);
          this.patronPhoneLocked =
            !!customPayment.capturePhone &&
            this.isPvPhoneComplete(this.patron.phone);

          if (customPayment.captureAddress) {
            const base = this.paymentDetails.address ?? {
              subCountryCode: '',
              city: null,
              countryCode: 'USA',
              postalCode: null,
              address1: null,
            };
            const fromPatron = this.patron.address;
            if (fromPatron) {
              this.paymentDetails.address = {
                address1: fromPatron.address1 ?? base.address1 ?? '',
                city: fromPatron.city ?? base.city ?? '',
                postalCode: fromPatron.postalCode ?? base.postalCode ?? '',
                subCountryCode:
                  fromPatron.subCountryCode ?? base.subCountryCode ?? '',
                countryCode:
                  fromPatron.countryCode ?? base.countryCode ?? 'USA',
              };
            } else {
              this.paymentDetails.address = base;
            }
            this.subCountryList = this.getSortedSubCountryList(
              this.paymentDetails.address.countryCode
            );
          }

          if (customPayment.capturePhone) {
            if (!this.paymentDetails.phone) {
              this.paymentDetails.phone = {
                phone: '',
                countryCode: 'USA',
              };
            }
            const pp = this.patron.phone;
            if (pp) {
              this.paymentDetails.phone = {
                phone: pp.phone ?? '',
                countryCode:
                  pp.countryCode ??
                  this.paymentDetails.phone.countryCode ??
                  'USA',
              };
            }
          }
        },
        error: error => {
          const modalData = {
            title: 'ERROR',
            content:
              'The MLB account used does not exists for this club entity. Please try again with another account.',
            acceptBtnName: 'CLOSE',
            acceptFunction: () => undefined,
          };

          if (error instanceof ApiException) {
            modalData.content = error.message;

            if (!error.data?.['isPatronNotFounError']) {
              modalData['acceptFunction'] = () => {
                this.restartTransactionAfterError();
              };
            }
          }
          this.modalsService.openModal(modalData);
        },
      });
    this.subscriptions.push(subscription);
  }

  onChangeDigitalWalletSelector(): void {
    if (!this.paymentDetails.digitalWallet) {
      return;
    }
    this.paymentDetails.cardType =
      this.paymentDetails.digitalWallet.cardType.description || '';
  }

  checkboxPayInFullChange(instance: Event): void {
    const isChecked = <HTMLInputElement>instance.currentTarget;
    const balance =
      this.transaction()!.transaction.tdcTransaction?.balance || 0;
    const patronAccountCredit = this.patron?.accountCredit || 0;

    if (isChecked && patronAccountCredit >= balance) {
      this.paymentDetails.accountCredit.credits = balance;
    } else if (isChecked && patronAccountCredit <= balance) {
      this.paymentDetails.accountCredit.credits = patronAccountCredit;
    } else if (!isChecked) {
      this.paymentDetails.accountCredit.credits = 0;
    }

    this.formatForDisplay();
  }

  /** Used only by the patron-association error retry (no cart restore) — distinct from the header's `restartTransaction()`. */
  private restartTransactionAfterError() {
    this.analyticsService.setPendingCancelReason(
      ANALYTICS_CANCEL_REASONS.PATRON_ASSOCIATION_ERROR
    );
    // Same reasoning as onTransactionExpiredAction(): this is a cancellation, not an
    // abandonment — disarm so ngOnDestroy doesn't also fire checkout_abandoned.
    this.analyticsService.setPendingAbandon(
      ANALYTICS_ABANDON_OWNERS.CHECKOUT,
      null
    );

    const subscription = this.connectionService
      .deleteTransaction(this.transactionId)
      .subscribe({
        next: response => {
          this.goSeatSelection();
        },
        error: error => {
          this.goSeatSelection();
        },
      });
    this.subscriptions.push(subscription);
  }

  onBlurInputCredits() {
    const formattedValueTransformed = this.currencyPipe.transform(
      this.paymentDetails.accountCredit.credits,
      'USD',
      'symbol',
      '1.2-2'
    ) as string;
    const formattedValue = formattedValueTransformed
      ?.replace(/\$/g, '')
      .replace(/,/g, '');
    const patronAccountCredit = this.patron?.accountCredit || 0;

    this.paymentDetails.accountCredit.credits = +formattedValue;

    if (+this.paymentDetails.accountCredit.credits > patronAccountCredit) {
      this.paymentDetails.accountCredit.credits = +patronAccountCredit;
    }
  }

  onCreditsChange(value: string) {
    const numericValue = parseFloat(value.replace(/,/g, ''));

    this.paymentDetails.accountCredit.credits = isNaN(numericValue)
      ? 0
      : numericValue;

    this.displayCredits = value;
  }

  formatForDisplay() {
    // Format the number with comma separators and two decimal places for display
    this.displayCredits =
      this.paymentDetails.accountCredit.credits.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  }

  onChangeAddressCountry(countryCode: string) {
    this.subCountryList = this.getSortedSubCountryList(countryCode);
  }

  private getSortedSubCountryList(countryCode: string): SubCountryItem[] {
    const list = this.subCountryObject[countryCode] ?? [];
    return [...list].sort((a, b) =>
      a.sub_country_name.localeCompare(b.sub_country_name)
    );
  }

  /** Snapshot from PV patron payload — not live form state */
  private isPvAddressComplete(addr: PatronModel['address']): boolean {
    if (!addr) {
      return false;
    }
    return !!(
      (addr.address1 ?? '').trim() &&
      (addr.city ?? '').trim() &&
      (addr.postalCode ?? '').trim() &&
      (addr.subCountryCode ?? '').trim() &&
      (addr.countryCode ?? '').trim()
    );
  }

  /** Snapshot from PV patron payload — not live form state */
  private isPvPhoneComplete(phone: PatronModel['phone']): boolean {
    if (!phone) {
      return false;
    }
    return !!(phone.phone ?? '').trim() && !!(phone.countryCode ?? '').trim();
  }
}
